import React, { useMemo, useState } from 'react'
import { MalindexBesok, MalindexConfig, MalindexMal, MalindexOmrade, SparaBesok } from '../../../api/malindex/malindexApi'
import { tillFlodesSvar } from '../malindexCalc'
import FordjupningOchMal from './FordjupningOchMal'
import FragorMatris from './FragorMatris'
import PrioriteradeOmraden from './PrioriteradeOmraden'
import StegKnappar from './StegKnappar'
import StegRad from './StegRad'
import { arIfyllt, ofullstandigaMal } from './MalRedigerare'
import ArendeIdModal from './ArendeIdModal'
import AvbrytModal from './AvbrytModal'
import UppstartModal, { UppstartVarden } from './UppstartModal'

/**
 * Behovsmätning – kartläggningsbesöket (besök 1).
 *
 * Uppstarten tas i en modal innan flödet börjar. Sedan tre steg:
 *   1  Behovsfrågorna
 *   2  Prioriteringen – områdena rangordnade efter svaren, stjärnmarkera de viktigaste
 *   3  Fördjupningsfrågor för de stjärnmärkta, som stöd, + målformulering
 *
 * Besöket innehåller ingen nyttoskattning och ger därför inget index – det finns
 * ingenting att vikta ännu.
 */

export interface BehovsmatningProps {
  config: MalindexConfig
  personnummer: string
  /** Ett pausat besök som återupptas – flödet startar där det slutade. */
  utkast?: MalindexBesok | null
  /** Anroparen sätter pnr innan anropet går vidare till servern. */
  onSpara: (payload: SparaBesok) => Promise<void>
  onAvbryt: () => void
  sparar?: boolean
}

const STEG = ['Behovsfrågor', 'Prioritering', 'Fördjupning & mål']

const Behovsmatning: React.FC<BehovsmatningProps> = ({
  config,
  personnummer,
  utkast,
  onSpara,
  onAvbryt,
  sparar,
}) => {
  // Allt initialtillstånd kommer ur utkastet när ett sådant återupptas. Uppstarten är
  // undantaget: den frågas om, eftersom dagen och sällskapet kan vara andra nu.
  const [steg, setSteg] = useState(utkast?.utkast_steg ?? 0)
  const [uppstart, setUppstart] = useState<UppstartVarden | null>(null)
  const [svar, setSvar] = useState<Record<number, number | undefined>>(() => tillFlodesSvar(utkast?.behov_svar))
  const [prioriterade, setPrioriterade] = useState<number[]>(utkast?.prioriterade ?? [])
  const [mal, setMal] = useState<MalindexMal[]>(utkast?.mal ?? [])
  const [visaArendeId, setVisaArendeId] = useState(false)
  const [visaAvbryt, setVisaAvbryt] = useState(false)
  const [note, setNote] = useState(utkast?.note ?? '')

  const maxPrio = config.maxPrioriterade

  const togglePrio = (id: number) =>
    setPrioriterade((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < maxPrio ? [...p, id] : p))

  const valjSvar = (id: number, varde: number) => {
    setSvar((s) => ({ ...s, [id]: varde }))
    // Ett område som markerats "ej tillämpligt" kan inte vara prioriterat.
    if (varde === config.ejTillampligt.varde) setPrioriterade((p) => p.filter((x) => x !== id))
  }

  /**
   * Alla områden måste besvaras innan steget kan lämnas. "Ej tillämpligt" finns just
   * för att det alltid ska gå att svara – ett tomt fält betyder därför att området
   * missades, inte att frågan inte passade.
   */
  const allaBesvarade = useMemo(
    () => config.omraden.every((o) => svar[o.id] !== undefined),
    [config.omraden, svar]
  )

  const prioOmraden: MalindexOmrade[] = useMemo(
    () => config.omraden.filter((o) => prioriterade.includes(o.id)),
    [config.omraden, prioriterade]
  )

  /** Målformuleringen är obligatorisk – påbörjade mål utan den låser sparandet. */
  const halvfardigaMal = ofullstandigaMal(mal)

  /**
   * Samma nyttolast oavsett om besöket sparas eller pausas – skillnaden är `draft`.
   * Ett utkast bär dessutom sitt steg, och sitt id om det redan finns en rad att
   * uppdatera. Halvfyllda mål får följa med i ett utkast; vid ett riktigt sparande är
   * de omöjliga, eftersom knappen är låst då.
   */
  const byggPayload = (u: UppstartVarden, arendeId: string | null, draft: boolean): SparaBesok => {
    const behovSvar: Record<string, number> = {}
    config.omraden.forEach((o) => {
      const v = svar[o.id]
      if (v !== undefined) behovSvar[String(o.id)] = v
    })
    const trimmad = note.trim()

    return {
      id: utkast?.id ?? null,
      pnr: '',
      pid: arendeId || null,
      // Vad handläggaren valde att starta, inte något som härleds efteråt.
      besokstyp: 'kartlaggning',
      kanal: u.kanal,
      svarande: u.svarande,
      demensdiagnos: u.demensdiagnos,
      behovSvar,
      nyttaSvar: null,
      prioriterade,
      mal: draft ? mal : mal.filter(arIfyllt),
      malUppfoljning: [],
      draft,
      utkastSteg: draft ? steg : null,
      note: trimmad.length > 0 ? trimmad : undefined,
    }
  }

  const handleSpara = (arendeId: string | null) => {
    if (sparar || !uppstart) return
    onSpara(byggPayload(uppstart, arendeId, false))
  }

  /** Pausar besöket. Inget ÄrendeID efterfrågas – det hör till ett färdigt besök. */
  const handlePausa = () => {
    if (sparar || !uppstart) return
    onSpara(byggPayload(uppstart, null, true))
  }

  // Modalen är grinden: flödet renderas först när uppstarten är gjord.
  if (!uppstart) {
    return (
      <UppstartModal
        config={config}
        open
        besokstyp="kartlaggning"
        personnummer={personnummer}
        aterupptar={!!utkast}
        tidigareDemensdiagnos={utkast?.demensdiagnos}
        onKlar={setUppstart}
        onAvbryt={onAvbryt}
      />
    )
  }

  return (
    // Flödet fyller skärmen så att knappraden kan tryckas ned till botten.
    <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 96px)' }}>
      <StegRad steg={STEG} aktivt={steg} />

      {steg === 0 && (
        <div className="flex flex-1 flex-col">
          <FragorMatris
            leadIn={config.behovLeadIn}
            omraden={config.omraden}
            skala={config.skala}
            ejTillampligt={config.ejTillampligt}
            variant="behov"
            svar={svar}
            onValj={valjSvar}
          />

          <StegKnappar
            knappar={[
              {
                etikett: 'Nästa',
                onClick: () => setSteg(1),
                variant: 'primar',
                disabled: !allaBesvarade,
                title: allaBesvarade ? '' : 'Alla områden måste besvaras',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </div>
      )}

      {steg === 1 && (
        <div className="flex flex-1 flex-col">
          <PrioriteradeOmraden config={config} svar={svar} prioriterade={prioriterade} onToggle={togglePrio} />

          <StegKnappar
            knappar={[
              { etikett: 'Tillbaka', onClick: () => setSteg(0) },
              {
                etikett: 'Nästa',
                onClick: () => setSteg(2),
                variant: 'primar',
                disabled: prioriterade.length === 0,
                title: prioriterade.length === 0 ? 'Markera minst ett prioriterat område' : '',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </div>
      )}

      {steg === 2 && (
        <>
          <FordjupningOchMal
            config={config}
            prioOmraden={prioOmraden}
            mal={mal}
            onAndraMal={setMal}
            note={note}
            onAndraNote={setNote}
          />
          <StegKnappar
            knappar={[
              { etikett: 'Tillbaka', onClick: () => setSteg(1) },
              {
                etikett: sparar ? 'Sparar…' : 'Spara besök',
                onClick: () => setVisaArendeId(true),
                variant: 'primar',
                disabled: sparar || halvfardigaMal.length > 0,
                title: halvfardigaMal.length > 0 ? 'Formulera målet, eller ta bort det' : '',
              },
              { etikett: 'Avbryt besök', onClick: () => setVisaAvbryt(true) },
            ]}
          />
        </>
      )}

      <AvbrytModal
        open={visaAvbryt}
        personnummer={personnummer}
        sparar={sparar}
        onPausa={handlePausa}
        onKasta={onAvbryt}
        onFortsatt={() => setVisaAvbryt(false)}
      />

      <ArendeIdModal
        open={visaArendeId}
        sparar={sparar}
        onBekrafta={handleSpara}
        onAvbryt={() => setVisaArendeId(false)}
      />
    </div>
  )
}

export default Behovsmatning
